import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { useDebounce } from '../../../hooks/useDebounce'
import { api } from '../../../api/axios'

// Consulta de Inscricoes: totais de inscricoes (numero 1) e reinscricoes (2 em diante) por
// modalidade e por atirador (rota /inscricoes/consulta da API). Canceladas nao entram. So leitura.

type Tipo = 'todas' | 'inscricoes' | 'reinscricoes'
interface Contagem { inscricoes: number; reinscricoes: number; total: number; valor: number }
interface Consulta {
  resumo: Contagem & { atiradores: number }
  modalidades: Array<Contagem & { idmodalidade: number; nome: string; grupo: string | null; atiradores: number }>
  atiradores: Array<Contagem & { idatirador: number; nome: string; cpf: string | null; modalidades: Array<Contagem & { idmodalidade: number; nome: string }> }>
}

const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))

export function ConsultaInscricoesPage() {
  const [idcampeonato, setIdcampeonato] = useState<number | ''>('')
  const [numeroEtapa, setNumeroEtapa] = useState<number | ''>('')
  const [idmodalidade, setIdmodalidade] = useState<number | ''>('')
  const [tipo, setTipo] = useState<Tipo>('todas')
  const [atirador, setAtirador] = useState<{ idatirador: number; nome: string } | null>(null)
  const [busca, setBusca] = useState('')
  const [sugestoes, setSugestoes] = useState(false)

  const { data: campeonatosData } = useCampeonatos({ limit: 100 })
  const { data: modalidadesData } = useModalidades(idcampeonato ? { idcampeonato, limit: 100 } : { limit: 100 })
  const buscaDebounced = useDebounce(busca, 400)
  const { data: atiradoresData, isLoading: buscando } = useAtiradores(buscaDebounced.length >= 3 ? { nome: buscaDebounced, limit: 10 } : {})
  const atiradoresBusca = buscaDebounced.length >= 3 ? atiradoresData?.data ?? [] : []

  const filtros = {
    ...(idcampeonato ? { idcampeonato } : {}),
    ...(numeroEtapa ? { numero_etapa: numeroEtapa } : {}),
    ...(idmodalidade ? { idmodalidade } : {}),
    ...(atirador ? { idatirador: atirador.idatirador } : {}),
    tipo,
  }
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inscricoes-consulta', filtros],
    queryFn: async () => (await api.get<{ data: Consulta }>('/inscricoes/consulta', { params: filtros })).data.data,
  })

  const campeonatoNome = campeonatosData?.data?.find(c => Number(c.idcampeonato) === Number(idcampeonato))?.descricao
  const modalidadeNome = modalidadesData?.data?.find(m => Number(m.idmodalidade) === Number(idmodalidade))?.nome
  const tipoNome = { todas: 'Inscricoes e reinscricoes', inscricoes: 'So inscricoes', reinscricoes: 'So reinscricoes' }[tipo]

  function limpar() {
    setIdcampeonato(''); setNumeroEtapa(''); setIdmodalidade(''); setTipo('todas'); setAtirador(null); setBusca('')
  }

  function imprimir() {
    if (!data) return
    const linha = (c: Contagem) => `<td class="n">${c.inscricoes}</td><td class="n">${c.reinscricoes}</td><td class="n"><b>${c.total}</b></td><td class="n">${reais(c.valor)}</td>`
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Consulta de Inscricoes</title><style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:16px} h1{font-size:16px;margin:0 0 4px} h2{font-size:13px;margin:16px 0 4px}
      table{border-collapse:collapse;width:100%} th,td{border:1px solid #999;padding:3px 5px;text-align:left} th{background:#eee}
      .n{text-align:right} .sub{color:#555;font-size:11px} tfoot td{font-weight:bold;background:#f5f5f5}</style></head><body>
      <h1>Consulta de Inscricoes</h1>
      <div class="sub">${esc(campeonatoNome ?? 'Todos os campeonatos')} - ${numeroEtapa ? `${numeroEtapa}a etapa` : 'todas as etapas'} - ${esc(modalidadeNome ?? 'todas as modalidades')} - ${esc(atirador?.nome ?? 'todos os atiradores')} - ${tipoNome}</div>
      <h2>Resumo</h2><table><tr><th>Inscritos</th><th>Inscricoes</th><th>Reinscricoes</th><th>Total</th><th>Valor</th></tr>
      <tr><td class="n">${data.resumo.atiradores}</td>${linha(data.resumo)}</tr></table>
      <h2>Por modalidade</h2><table><thead><tr><th>Modalidade</th><th>Inscritos</th><th>Inscricoes</th><th>Reinscricoes</th><th>Total</th><th>Valor</th></tr></thead><tbody>
      ${data.modalidades.map(m => `<tr><td>${esc(m.nome)}</td><td class="n">${m.atiradores}</td>${linha(m)}</tr>`).join('')}</tbody>
      <tfoot><tr><td>TOTAL</td><td class="n">${data.resumo.atiradores}</td>${linha(data.resumo)}</tr></tfoot></table>
      <h2>Por atirador</h2><table><thead><tr><th>Atirador</th><th>Modalidade</th><th>Inscricoes</th><th>Reinscricoes</th><th>Total</th><th>Valor</th></tr></thead><tbody>
      ${data.atiradores.map(a => a.modalidades.map((m, i) => `<tr>${i === 0 ? `<td rowspan="${a.modalidades.length + 1}">${esc(a.nome)}</td>` : ''}<td>${esc(m.nome)}</td>${linha(m)}</tr>`).join('') + `<tr><td><b>Total do atirador</b></td>${linha(a)}</tr>`).join('')}
      </tbody><tfoot><tr><td colspan="2">TOTAL (${data.resumo.atiradores} atiradores)</td>${linha(data.resumo)}</tr></tfoot></table>
      <script>window.onload=function(){window.print()}</script></body></html>`
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(html); w.document.close()
  }

  const cartao = (titulo: string, valor: string | number, destaque = false) => (
    <div className={`rounded-xl border p-4 ${destaque ? 'border-compumaq-dourado bg-compumaq-dourado/10' : 'border-compumaq-dourado/25 bg-compumaq-pretoSuave'}`}>
      <div className="text-xs text-compumaq-textoSuave">{titulo}</div>
      <div className="text-2xl font-bold text-compumaq-texto">{valor}</div>
    </div>
  )
  const th = 'px-3 py-2 text-left text-xs font-semibold text-compumaq-textoSuave uppercase'
  const td = 'px-3 py-2 text-sm'
  const tdn = 'px-3 py-2 text-sm text-right tabular-nums'

  return (
    <div className="space-y-4">
      <PageHeader
        title="Consulta de Inscricoes"
        description="Inscricoes e reinscricoes por atirador e por modalidade"
        action={
          <button onClick={imprimir} disabled={!data} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-40">
            Imprimir
          </button>
        }
      />

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select aria-label="Campeonato" className={selectClass} value={idcampeonato} onChange={e => { setIdcampeonato(e.target.value ? parseInt(e.target.value) : ''); setIdmodalidade('') }}>
              <option value="">Todos</option>
              {campeonatosData?.data?.map(c => <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao}</option>)}
            </select>
          </div>
          <div className="w-40">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
            <select aria-label="Etapa" className={selectClass} value={numeroEtapa} onChange={e => setNumeroEtapa(e.target.value ? parseInt(e.target.value) : '')}>
              <option value="">Todas as etapas</option>
              {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}ª Etapa</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
            <select aria-label="Modalidade" className={selectClass} value={idmodalidade} onChange={e => setIdmodalidade(e.target.value ? parseInt(e.target.value) : '')}>
              <option value="">Todas</option>
              {(modalidadesData?.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome)).map(m => <option key={m.idmodalidade} value={m.idmodalidade}>{m.nome}</option>)}
            </select>
          </div>
          <div className="w-52">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Tipo</label>
            <select aria-label="Tipo" className={selectClass} value={tipo} onChange={e => setTipo(e.target.value as Tipo)}>
              <option value="todas">Inscricoes e reinscricoes</option>
              <option value="inscricoes">So inscricoes</option>
              <option value="reinscricoes">So reinscricoes</option>
            </select>
          </div>
          <div className="flex-1 min-w-[240px] relative">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
            {atirador ? (
              <div className="flex items-center gap-2">
                <div data-atirador-escolhido className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">{atirador.nome}</div>
                <button onClick={() => { setAtirador(null); setBusca('') }} className="px-3 py-2 text-sm text-red-600 hover:text-red-800 border border-compumaq-dourado/25 rounded-lg hover:bg-compumaq-preto">Todos</button>
              </div>
            ) : (
              <div className="relative">
                <input type="text" placeholder="Todos (digite para escolher um)" className={inputClass} value={busca}
                  onChange={e => { setBusca(e.target.value); setSugestoes(true) }} onFocus={() => setSugestoes(true)} />
                {buscando && buscaDebounced.length >= 3 && <div className="absolute right-3 top-2.5"><Spinner size="sm" /></div>}
                {sugestoes && atiradoresBusca.length > 0 && (
                  <div className="absolute z-10 w-full bg-compumaq-pretoSuave border border-compumaq-dourado/25 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {atiradoresBusca.map(a => (
                      <button key={a.idatirador} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-compumaq-preto border-b border-compumaq-dourado/25 last:border-0"
                        onClick={() => { setAtirador({ idatirador: Number(a.idatirador), nome: a.nome }); setBusca(a.nome); setSugestoes(false) }}>
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-compumaq-textoSuave text-xs ml-2">{a.cpf}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button onClick={limpar} className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Limpar</button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : isError || !data ? (
        <div className="text-sm text-red-600">Nao foi possivel carregar a consulta.</div>
      ) : (
        <>
          <div data-resumo className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {cartao('Inscritos (atiradores)', data.resumo.atiradores)}
            {cartao('Inscricoes', data.resumo.inscricoes)}
            {cartao('Reinscricoes', data.resumo.reinscricoes)}
            {cartao('Total', data.resumo.total, true)}
            {cartao('Valor', reais(data.resumo.valor))}
          </div>

          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-x-auto">
            <h2 className="px-4 pt-3 font-semibold text-compumaq-texto">Por modalidade</h2>
            <table data-por-modalidade className="w-full mt-2">
              <thead className="border-b border-compumaq-dourado/25">
                <tr><th className={th}>Modalidade</th><th className={`${th} text-right`}>Inscritos</th><th className={`${th} text-right`}>Inscricoes</th><th className={`${th} text-right`}>Reinscricoes</th><th className={`${th} text-right`}>Total</th><th className={`${th} text-right`}>Valor</th></tr>
              </thead>
              <tbody>
                {data.modalidades.length === 0 && <tr><td colSpan={6} className={`${td} text-compumaq-textoSuave`}>Nenhuma inscricao com esses filtros.</td></tr>}
                {data.modalidades.map(m => (
                  <tr key={m.idmodalidade} className="border-b border-compumaq-dourado/10">
                    <td className={td}>{m.nome}</td><td className={tdn}>{m.atiradores}</td><td className={tdn}>{m.inscricoes}</td><td className={tdn}>{m.reinscricoes}</td><td className={`${tdn} font-semibold`}>{m.total}</td><td className={tdn}>{reais(m.valor)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-compumaq-dourado/10 font-bold">
                  <td className={td}>TOTAL</td><td className={tdn}>{data.resumo.atiradores}</td><td className={tdn}>{data.resumo.inscricoes}</td><td className={tdn}>{data.resumo.reinscricoes}</td><td className={tdn}>{data.resumo.total}</td><td className={tdn}>{reais(data.resumo.valor)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-x-auto">
            <h2 className="px-4 pt-3 font-semibold text-compumaq-texto">Por atirador ({data.atiradores.length})</h2>
            <table data-por-atirador className="w-full mt-2">
              <thead className="border-b border-compumaq-dourado/25">
                <tr><th className={th}>Atirador</th><th className={th}>Modalidades</th><th className={`${th} text-right`}>Inscricoes</th><th className={`${th} text-right`}>Reinscricoes</th><th className={`${th} text-right`}>Total</th><th className={`${th} text-right`}>Valor</th></tr>
              </thead>
              <tbody>
                {data.atiradores.map(a => (
                  <tr key={a.idatirador} className="border-b border-compumaq-dourado/10 align-top">
                    <td className={`${td} font-medium`}>{a.nome}</td>
                    <td className={`${td} text-xs`}>
                      {a.modalidades.map(m => (
                        <div key={m.idmodalidade}>{m.nome}: <b>{m.total}</b>{m.reinscricoes > 0 && <span className="text-compumaq-textoSuave"> ({m.inscricoes} + {m.reinscricoes} reinscr.)</span>}</div>
                      ))}
                    </td>
                    <td className={tdn}>{a.inscricoes}</td><td className={tdn}>{a.reinscricoes}</td><td className={`${tdn} font-semibold`}>{a.total}</td><td className={tdn}>{reais(a.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
